import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { List, Tag } from "antd";
import { apiGetStrategyActivity } from "api/apiStrategies";
import dayjs from "dayjs";
import { returnUpDownClassname, toFixed } from "utils/helpers";
import { ActivityTypes, ActivityProperties } from "./ActivityTypes";

const ActivityTime = (props) => {
  const { time } = props;
  return (
    <Tag className="w-[120px]">{dayjs(time).format("DD/MM/YY HH:mm:ss")}</Tag>
  );
};

const Description = (props) => {
  const { label, children } = props;
  return (
    <span className="ps-1">
      <strong>{label}: </strong>
      <span className="text-[#ffffff]">{children}</span>
    </span>
  );
};

// activity data can be partial (e.g. an option position without a quote that
// day, or a deleted deal) -- never let one entry break the card
const fmt = (value) =>
  value === undefined || value === null || Number.isNaN(Number(value))
    ? "-"
    : toFixed(Number(value));

const SKIP_REASONS = {
  "excluded-weekday": "Entry weekday is excluded",
  "max-open-positions": "Max open positions reached",
  "pending-buy-exists": "A pending buy is waiting",
  "no-take-profit-set": "No take profit set",
  "no-expiry-in-window": "No expiry in the bars-to-expiry window",
  "no-ask-price": "No ask price for the contract",
  "budget-below-one-contract": "Order size is below one contract",
  "stale-prediction": "No prediction for today",
  "price-adjustment-pending": "Split pending -- waiting for the repair",
};

const StrategyActivityCard = (props) => {
  const { selectedStrategy } = props;
  const [strategyActivity, setStrategyActivity] = useState([]);

  const fetchStrategyActivity = async () => {
    const response = await apiGetStrategyActivity(selectedStrategy._id);
    setStrategyActivity(response);
  };

  const returnContent = (activity) => {
    switch (activity.type) {
      case ActivityTypes.trendOpenNotify: {
        return (
          <div>
            <Description label="Buy point">
              {fmt(activity.data?.deal?.advicedBuyPrice)}
            </Description>
            {" |"}
            <Description label="Contracts">
              {fmt(activity.data?.deal?.advicedBuyAmount)}
            </Description>
            {/* on{" "}
          {dayjs(
            new Date(activity.data.deal.advicedBuyDate).toDateString()
          )
            .add(+23, "h")
            .format("DD/MM/YY HH:mm")} */}
          </div>
        );
      }
      case ActivityTypes.tradeOpen: {
        return (
          <div>
            <Description label="Open rate">
              {fmt(activity.data?.deal?.openRate)}
            </Description>
            {" |"}
            <Description label="Contracts">
              {fmt(activity.data?.deal?.amount)}
            </Description>
            {/* on{" "}
          {dayjs(activity.data.deal.openDate).format("DD/MM/YY HH:mm")} */}
          </div>
        );
      }
      case ActivityTypes.tradeClose: {
        return (
          <div>
            <Description label="PNL">
              <span
                className={returnUpDownClassname(
                  activity.data?.deal?.profit?.value,
                )}
              >
                {fmt(activity.data?.deal?.profit?.perc)}% $
                {fmt(activity.data?.deal?.profit?.value)}
              </span>
            </Description>
            {" |"}
            <Description label="Close rate">
              {fmt(activity.data?.deal?.closeRate)}
            </Description>
            {" |"}
            <Description label="Contracts">
              {fmt(activity.data?.deal?.amount)}
            </Description>
            {/* on {dayjs(activity.data.deal.closeDate).format("DD/MM/YY")} */}
          </div>
        );
      }
      case ActivityTypes.deletePendingBuy: {
        return (
          <div>
            <Description label="Buy point">
              {fmt(activity.data?.deal?.advicedBuyPrice)}
            </Description>
            {" |"}
            <Description label="Contracts">
              {fmt(activity.data?.deal?.advicedBuyAmount)}
            </Description>
          </div>
        );
      }
      case ActivityTypes.missedSellOpportunity: {
        return (
          <div>
            <Description label="Rate">
              {fmt(activity.data?.dealSnapshot?.currentRate)} |
            </Description>
            {" |"}
            <Description label="Sell point">
              {fmt(activity.data?.dealSnapshot?.advicedSellPrice)} |
            </Description>
            {" |"}
            <Description label="Contracts">
              {fmt(activity.data?.dealSnapshot?.amount)}
            </Description>
          </div>
        );
      }
      case ActivityTypes.tradeCloseNotify: {
        return (
          <div>
            <Description label="Sell point">
              {fmt(activity.data?.dealSnapshot?.advicedSellPrice)} |
            </Description>
            {" |"}
            <Description label="Contracts">
              {fmt(activity.data?.dealSnapshot?.amount)}
            </Description>
          </div>
        );
      }
      case ActivityTypes.tradeUpdate: {
        return (
          <div>
            <Description label="PNL">
              <span
                className={returnUpDownClassname(
                  activity.data?.dealSnapshot?.profit?.value,
                )}
              >
                {fmt(activity.data?.dealSnapshot?.profit?.perc)}% $
                {fmt(activity.data?.dealSnapshot?.profit?.value)}
              </span>
            </Description>
            {" |"}
            <Description label="Rate">
              {fmt(activity.data?.dealSnapshot?.currentRate)}
            </Description>
          </div>
        );
      }
      case ActivityTypes.buySignal: {
        return (
          <div>
            <Description label="15:50 close">
              {fmt(activity.data?.signal?.close)}
            </Description>
            {" |"}
            <Description label="Buys in a row">
              {activity.data?.buyPredictionsInRow ?? "-"}
            </Description>
          </div>
        );
      }
      case ActivityTypes.signalSkipped: {
        const { reason, budget, contractCost } = activity.data || {};
        return (
          <div>
            {SKIP_REASONS[reason] || reason}
            {budget !== undefined && (
              <>
                {" |"}
                <Description label="Budget">${fmt(budget)}</Description>
                {" |"}
                <Description label="1 contract">
                  ${fmt(contractCost)}
                </Description>
              </>
            )}
          </div>
        );
      }
      case ActivityTypes.splitAdjusted: {
        const { event, ratio, trades = [] } = activity.data || {};
        return (
          <div>
            {event}
            {" |"}
            <Description label="Shares per old share">{fmt(ratio)}</Description>
            {" |"}
            <Description label="Trades adjusted">{trades.length}</Description>
          </div>
        );
      }
      case ActivityTypes.automatedTradeOpenFail: {
        return <div>Failed to open trade with automation</div>;
      }
      case ActivityTypes.automatedTradeCloseFail: {
        return <div>Failed to close trade with automation</div>;
      }
      default: {
        return <div />;
      }
    }
  };

  useEffect(() => {
    fetchStrategyActivity();
  }, []);

  return (
    <GenericCard
      title="Strategy Activity"
      className="table-card"
      maxHeight={800}
    >
      <List
        pagination={{
          position: "bottom",
          align: "center",
          defaultPageSize: 8,
          showSizeChanger: true,
        }}
        size="small"
        dataSource={strategyActivity}
        renderItem={(item) => (
          <List.Item extra={<ActivityTime time={item.created} />}>
            <List.Item.Meta
              title={
                ActivityProperties[item.type] ? (
                  <Tag
                    color={ActivityProperties[item.type]?.color}
                    icon={ActivityProperties[item.type]?.icon}
                  >
                    {ActivityProperties[item.type]?.title}
                  </Tag>
                ) : (
                  <Tag color="default">{item.type}</Tag>
                )
              }
              description={returnContent(item)}
            />
          </List.Item>
        )}
      />
    </GenericCard>
  );
};

StrategyActivityCard.propTypes = {
  selectedStrategy: PropTypes.object.isRequired,
};

export default StrategyActivityCard;
